import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import {
  Search, FileText, CheckCircle, XCircle, Clock, Eye, RefreshCw, Download,
  ZoomIn, ZoomOut, RotateCw, Maximize, X, Image as ImageIcon, Package, AlertTriangle,
} from 'lucide-react';

const KYC_BADGE = {
  pending: 'bg-yellow-500/20 text-yellow-600 border-yellow-500/30',
  submitted: 'bg-yellow-500/20 text-yellow-600 border-yellow-500/30',
  under_review: 'bg-blue-500/20 text-blue-500 border-blue-500/30',
  approved: 'bg-green-500/20 text-green-600 border-green-500/30',
  rejected: 'bg-red-500/20 text-red-500 border-red-500/30',
  reupload_required: 'bg-orange-500/20 text-orange-500 border-orange-500/30',
};

const DOC_BADGE = {
  uploaded: 'bg-slate-500/15 text-slate-500 border-slate-500/30',
  under_review: 'bg-blue-500/15 text-blue-500 border-blue-500/30',
  verified: 'bg-green-500/15 text-green-600 border-green-500/30',
  rejected: 'bg-red-500/15 text-red-500 border-red-500/30',
  reupload_required: 'bg-orange-500/15 text-orange-500 border-orange-500/30',
};

const Stat = ({ label, value, icon: Icon, testid }) => (
  <Card data-testid={testid}><CardContent className="p-4">
    <div className="flex items-center gap-2 text-muted-foreground text-xs mb-1"><Icon className="h-4 w-4" />{label}</div>
    <p className="text-2xl font-bold font-mono">{value}</p>
  </CardContent></Card>
);

export default function KYCReviewPage() {
  const { api } = useAuth();
  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ kyc_status: 'all', entity_type: 'all', risk_category: 'all', q: '' });

  const [detail, setDetail] = useState(null);
  const [showDetail, setShowDetail] = useState(false);
  const [decision, setDecision] = useState('');
  const [remarks, setRemarks] = useState('');
  const [busy, setBusy] = useState(false);
  const [downloadingAll, setDownloadingAll] = useState(false);

  // Image/PDF viewer
  const [viewer, setViewer] = useState(null); // { doc, blobUrl }
  const [scale, setScale] = useState(1);
  const [rotation, setRotation] = useState(0);

  // Per-document status dialog
  const [docAction, setDocAction] = useState(null); // { doc, status }
  const [docReason, setDocReason] = useState('');

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (filters.kyc_status !== 'all') params.append('kyc_status', filters.kyc_status);
      if (filters.entity_type !== 'all') params.append('entity_type', filters.entity_type);
      if (filters.risk_category !== 'all') params.append('risk_category', filters.risk_category);
      if (filters.q) params.append('q', filters.q);
      const res = await api.get(`/admin/kyc/reviews?${params.toString()}`);
      setRows(res.data.reviews || []);
      setCounts(res.data.counts || {});
    } catch (e) {
      toast.error('Failed to load KYC review queue');
    } finally {
      setLoading(false);
    }
  }, [api, filters]);

  useEffect(() => {
    const t = setTimeout(load, 300);
    return () => clearTimeout(t);
  }, [load]);

  const openDetail = async (r) => {
    setShowDetail(true);
    setDetail(null);
    setDecision('');
    setRemarks('');
    try {
      const res = await api.get(`/admin/kyc/reviews/${r.merchant_id}`);
      setDetail(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load KYC detail');
      setShowDetail(false);
    }
  };

  const fetchBlob = useCallback(async (url) => {
    const res = await api.get(url.replace('/api', ''), { responseType: 'blob' });
    return URL.createObjectURL(res.data);
  }, [api]);

  const openViewer = async (doc) => {
    try {
      setScale(1); setRotation(0);
      const blobUrl = await fetchBlob(doc.view_url);
      setViewer({ doc, blobUrl });
    } catch (e) {
      toast.error('Could not open document');
    }
  };

  const closeViewer = () => {
    if (viewer?.blobUrl) URL.revokeObjectURL(viewer.blobUrl);
    setViewer(null);
  };

  const downloadOne = async (doc) => {
    try {
      const res = await api.get(doc.download_url.replace('/api', ''), { responseType: 'blob' });
      const blobUrl = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = doc.original_filename || `${doc.doc_type}`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(blobUrl);
    } catch (e) {
      toast.error('Download failed');
    }
  };

  const downloadAll = async () => {
    if (!detail) return;
    setDownloadingAll(true);
    try {
      const res = await api.get(`/admin/kyc/reviews/${detail.merchant.merchant_id}/download-all`, { responseType: 'blob' });
      const blobUrl = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = `${detail.merchant.mid}-KYC.zip`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(blobUrl);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not download documents');
    } finally {
      setDownloadingAll(false);
    }
  };

  const applyDocStatus = async () => {
    if (!docAction) return;
    const { doc, status } = docAction;
    if ((status === 'rejected' || status === 'reupload_required') && !docReason.trim()) {
      toast.error('A reason is required'); return;
    }
    try {
      await api.post(`/admin/kyc/documents/${detail.merchant.merchant_id}/${doc.document_id}/status`, { status, reason: docReason });
      toast.success('Document status updated');
      setDocAction(null); setDocReason('');
      const res = await api.get(`/admin/kyc/reviews/${detail.merchant.merchant_id}`);
      setDetail(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update document');
    }
  };

  const submitDecision = async () => {
    if (!decision) { toast.error('Select a decision'); return; }
    if ((decision === 'reject' || decision === 'reupload') && !remarks.trim()) {
      toast.error('Remarks are required for reject / re-upload'); return;
    }
    setBusy(true);
    try {
      await api.post(`/admin/kyc/reviews/${detail.merchant.merchant_id}/decision`, { decision, remarks });
      toast.success('KYC decision recorded');
      setShowDetail(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to record decision');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="kyc-review-page">
      <div>
        <h1 className="text-2xl font-heading font-bold">KYC Review</h1>
        <p className="text-muted-foreground text-sm">Review merchant KYC applications — merchants who have not started KYC are not listed here</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <Stat label="Pending Review" value={counts.pending_review ?? 0} icon={Clock} testid="kyc-count-pending" />
        <Stat label="Approved" value={counts.approved ?? 0} icon={CheckCircle} testid="kyc-count-approved" />
        <Stat label="Rejected" value={counts.rejected ?? 0} icon={XCircle} testid="kyc-count-rejected" />
        <Stat label="Reupload" value={counts.reupload_required ?? 0} icon={RefreshCw} testid="kyc-count-reupload" />
        <Stat label="Not Started" value={counts.not_started ?? 0} icon={FileText} testid="kyc-count-notstarted" />
      </div>

      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-wrap gap-3">
            <div className="flex-1 min-w-[220px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input placeholder="Search by MID, business, email, PAN, phone..." value={filters.q}
                  onChange={(e) => setFilters({ ...filters, q: e.target.value })} className="pl-9 rounded-sm" data-testid="kyc-search" />
              </div>
            </div>
            <Select value={filters.kyc_status} onValueChange={(v) => setFilters({ ...filters, kyc_status: v })}>
              <SelectTrigger className="w-[170px] rounded-sm" data-testid="kyc-status-filter"><SelectValue placeholder="All KYC" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="submitted">Submitted</SelectItem>
                <SelectItem value="under_review">Under Review</SelectItem>
                <SelectItem value="approved">Approved</SelectItem>
                <SelectItem value="rejected">Rejected</SelectItem>
                <SelectItem value="reupload_required">Reupload Required</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filters.risk_category} onValueChange={(v) => setFilters({ ...filters, risk_category: v })}>
              <SelectTrigger className="w-[140px] rounded-sm" data-testid="kyc-risk-filter"><SelectValue placeholder="All Risk" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Risk</SelectItem>
                <SelectItem value="medium">Medium</SelectItem>
                <SelectItem value="high">High</SelectItem>
                <SelectItem value="extreme">Extreme</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[860px]">
              <thead>
                <tr className="border-b text-left text-xs uppercase tracking-wider text-muted-foreground">
                  <th className="py-3 px-4">MID</th>
                  <th className="py-3 px-4">Merchant</th>
                  <th className="py-3 px-4">Entity</th>
                  <th className="py-3 px-4">KYC Status</th>
                  <th className="py-3 px-4">Submitted</th>
                  <th className="py-3 px-4">Documents</th>
                  <th className="py-3 px-4">Reviewer</th>
                  <th className="py-3 px-4 text-right">Action</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  [...Array(4)].map((_, i) => <tr key={i} className="border-b">{[...Array(8)].map((_, j) => <td key={j} className="py-3 px-4"><Skeleton className="h-4 w-full" /></td>)}</tr>)
                ) : rows.length ? rows.map((r) => (
                  <tr key={r.merchant_id} className="border-b last:border-0 hover:bg-muted/40" data-testid={`kyc-row-${r.mid}`}>
                    <td className="py-3 px-4 font-mono text-xs font-semibold text-primary">{r.mid}</td>
                    <td className="py-3 px-4"><p className="font-medium">{r.business_name}</p><p className="text-xs text-muted-foreground">{r.email}</p></td>
                    <td className="py-3 px-4 capitalize text-xs">{(r.entity_type || '—').replace(/_/g, ' ')}</td>
                    <td className="py-3 px-4"><Badge variant="outline" className={`rounded-sm text-[10px] ${KYC_BADGE[r.kyc_status] || 'border-border'}`}>{(r.kyc_status || '').replace(/_/g, ' ').toUpperCase()}</Badge></td>
                    <td className="py-3 px-4 text-xs text-muted-foreground">{r.submitted_at ? new Date(r.submitted_at).toLocaleDateString('en-IN') : '—'}</td>
                    <td className="py-3 px-4">
                      {r.document_count > 0 ? (
                        <button onClick={() => openDetail(r)} className="inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline" data-testid={`kyc-docs-${r.mid}`}>
                          <FileText className="h-3.5 w-3.5" />{r.document_count} Document{r.document_count > 1 ? 's' : ''}
                        </button>
                      ) : (
                        <span className="text-xs text-muted-foreground" data-testid={`kyc-docs-${r.mid}`}>0 Documents</span>
                      )}
                    </td>
                    <td className="py-3 px-4 text-xs">{r.reviewer || 'Not Assigned'}</td>
                    <td className="py-3 px-4 text-right">
                      <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => openDetail(r)} data-testid={`kyc-review-${r.mid}`}><Eye className="h-3 w-3 mr-1" />Review</Button>
                    </td>
                  </tr>
                )) : (
                  <tr><td colSpan={8} className="text-center py-10 text-muted-foreground">No KYC applications in the queue</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* KYC Review Gallery */}
      <Dialog open={showDetail} onOpenChange={(o) => { if (!o) setShowDetail(false); }}>
        <DialogContent className="max-w-5xl max-h-[92vh] overflow-hidden flex flex-col p-0" data-testid="kyc-detail-dialog">
          <DialogHeader className="px-6 pt-6 pb-4 border-b">
            <DialogTitle className="flex items-center gap-2">KYC Review · <span className="font-mono text-primary text-sm">{detail?.merchant?.mid}</span></DialogTitle>
            <DialogDescription>{detail?.merchant?.business_name}</DialogDescription>
          </DialogHeader>

          {!detail ? <div className="p-6"><Skeleton className="h-72 w-full" /></div> : (
            <div className="flex-1 overflow-y-auto px-6 py-5 space-y-6">
              {/* Merchant + KYC meta */}
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <p className="text-xs uppercase tracking-wider text-muted-foreground mb-2">Merchant</p>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div><p className="text-xs text-muted-foreground">Email</p><p className="truncate">{detail.merchant.email || '—'}</p></div>
                    <div><p className="text-xs text-muted-foreground">Phone</p><p>{detail.merchant.phone || '—'}</p></div>
                    <div><p className="text-xs text-muted-foreground">Entity</p><p className="capitalize">{(detail.merchant.entity_type || '—').replace(/_/g, ' ')}</p></div>
                    <div><p className="text-xs text-muted-foreground">PAN</p><p className="font-mono">{detail.merchant.pan_number || '—'}</p></div>
                  </div>
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wider text-muted-foreground mb-2">KYC</p>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div><p className="text-xs text-muted-foreground">Status</p><Badge variant="outline" className={`rounded-sm text-[10px] ${KYC_BADGE[detail.kyc.status] || 'border-border'}`}>{(detail.kyc.status || '').replace(/_/g, ' ').toUpperCase()}</Badge></div>
                    <div><p className="text-xs text-muted-foreground">Submitted</p><p className="text-xs">{detail.kyc.submitted_at ? new Date(detail.kyc.submitted_at).toLocaleString('en-IN') : '—'}</p></div>
                    <div><p className="text-xs text-muted-foreground">Reviewer</p><p className="text-xs">{detail.kyc.reviewer || 'Not Assigned'}</p></div>
                    <div><p className="text-xs text-muted-foreground">Remarks</p><p className="text-xs">{detail.kyc.remarks || '—'}</p></div>
                  </div>
                </div>
              </div>

              {/* Document gallery */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <p className="text-xs uppercase tracking-wider text-muted-foreground">KYC Documents ({detail.documents.length})</p>
                  {detail.documents.length > 0 && (
                    <Button variant="outline" size="sm" className="h-8 rounded-sm text-xs" onClick={downloadAll} disabled={downloadingAll} data-testid="kyc-download-all">
                      <Package className="h-3.5 w-3.5 mr-1" />{downloadingAll ? 'Preparing...' : 'Download All (ZIP)'}
                    </Button>
                  )}
                </div>
                {detail.documents.length ? (
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                    {detail.documents.map((d) => (
                      <DocCard key={d.document_id} doc={d} fetchBlob={fetchBlob} onOpen={openViewer}
                        onDownload={downloadOne} onSetStatus={(status) => { setDocAction({ doc: d, status }); setDocReason(''); }} />
                    ))}
                  </div>
                ) : <p className="text-sm text-muted-foreground py-6 text-center border rounded-sm">No documents uploaded by this merchant</p>}
              </div>

              {/* Overall decision */}
              {detail.kyc.status !== 'approved' && (
                <div className="space-y-3 pt-4 border-t">
                  <Label className="text-xs uppercase tracking-wider">KYC Decision</Label>
                  <div className="flex flex-wrap gap-3 items-start">
                    <Select value={decision} onValueChange={setDecision}>
                      <SelectTrigger className="w-[220px] rounded-sm" data-testid="kyc-decision-select"><SelectValue placeholder="Select decision" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="under_review">Mark Under Review</SelectItem>
                        <SelectItem value="approve">Approve KYC</SelectItem>
                        <SelectItem value="reject">Reject KYC</SelectItem>
                        <SelectItem value="reupload">Request Re-upload</SelectItem>
                      </SelectContent>
                    </Select>
                    <Button onClick={submitDecision} disabled={busy} className="rounded-sm" data-testid="kyc-submit-decision">{busy ? 'Saving...' : 'Submit Decision'}</Button>
                  </div>
                  {(decision === 'reject' || decision === 'reupload') && (
                    <Textarea value={remarks} onChange={(e) => setRemarks(e.target.value)} placeholder="Reason / admin remarks (required)..." className="rounded-sm" data-testid="kyc-remarks" />
                  )}
                </div>
              )}
            </div>
          )}

          <DialogFooter className="px-6 py-4 border-t">
            <Button variant="outline" onClick={() => setShowDetail(false)} className="rounded-sm">Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Per-document status reason dialog */}
      <Dialog open={!!docAction} onOpenChange={(o) => { if (!o) setDocAction(null); }}>
        <DialogContent className="max-w-md" data-testid="doc-status-dialog">
          <DialogHeader>
            <DialogTitle className="capitalize">Mark document: {docAction?.status?.replace(/_/g, ' ')}</DialogTitle>
            <DialogDescription>{docAction?.doc?.doc_label}</DialogDescription>
          </DialogHeader>
          {(docAction?.status === 'rejected' || docAction?.status === 'reupload_required') && (
            <Textarea value={docReason} onChange={(e) => setDocReason(e.target.value)} placeholder="Reason (e.g. image unclear, document expired, details do not match)..." className="rounded-sm" data-testid="doc-status-reason" />
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDocAction(null)} className="rounded-sm">Cancel</Button>
            <Button onClick={applyDocStatus} className="rounded-sm" data-testid="doc-status-apply">Apply</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Full-screen document viewer */}
      {viewer && createPortal(
        <div className="fixed inset-0 z-[9999] bg-black/90 flex flex-col" style={{ pointerEvents: 'auto' }} data-testid="doc-viewer">
          <div className="flex items-center justify-between px-4 py-3 text-white/90 border-b border-white/10">
            <div className="flex items-center gap-2 min-w-0">
              <FileText className="h-4 w-4 shrink-0" />
              <span className="text-sm truncate">{viewer.doc.doc_label} · {viewer.doc.original_filename}</span>
            </div>
            <div className="flex items-center gap-1">
              {viewer.doc.is_image && (
                <>
                  <Button variant="ghost" size="icon" className="text-white hover:bg-white/10 h-8 w-8" onClick={() => setScale((s) => Math.max(0.25, s - 0.25))} data-testid="viewer-zoom-out"><ZoomOut className="h-4 w-4" /></Button>
                  <span className="text-xs w-12 text-center">{Math.round(scale * 100)}%</span>
                  <Button variant="ghost" size="icon" className="text-white hover:bg-white/10 h-8 w-8" onClick={() => setScale((s) => Math.min(5, s + 0.25))} data-testid="viewer-zoom-in"><ZoomIn className="h-4 w-4" /></Button>
                  <Button variant="ghost" size="icon" className="text-white hover:bg-white/10 h-8 w-8" onClick={() => setScale(1)} data-testid="viewer-fit"><Maximize className="h-4 w-4" /></Button>
                  <Button variant="ghost" size="icon" className="text-white hover:bg-white/10 h-8 w-8" onClick={() => setRotation((r) => (r + 90) % 360)} data-testid="viewer-rotate"><RotateCw className="h-4 w-4" /></Button>
                </>
              )}
              <Button variant="ghost" size="icon" className="text-white hover:bg-white/10 h-8 w-8" onClick={() => downloadOne(viewer.doc)} data-testid="viewer-download"><Download className="h-4 w-4" /></Button>
              <Button variant="ghost" size="icon" className="text-white hover:bg-white/10 h-8 w-8" onClick={closeViewer} data-testid="viewer-close"><X className="h-4 w-4" /></Button>
            </div>
          </div>
          <div className="flex-1 overflow-auto flex items-center justify-center p-4">
            {viewer.doc.is_image ? (
              <img src={viewer.blobUrl} alt={viewer.doc.doc_label}
                style={{ transform: `scale(${scale}) rotate(${rotation}deg)`, transition: 'transform 0.15s ease' }}
                className="max-w-none select-none" />
            ) : (
              <iframe title="pdf" src={viewer.blobUrl} className="w-full h-full bg-white rounded" />
            )}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}

function DocCard({ doc, fetchBlob, onOpen, onDownload, onSetStatus }) {
  const [thumb, setThumb] = useState(null);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    let created = null;
    if (doc.is_image && doc.available) {
      fetchBlob(doc.view_url).then((url) => {
        created = url;
        if (mounted.current) setThumb(url); else URL.revokeObjectURL(url);
      }).catch(() => {});
    }
    return () => { mounted.current = false; if (created) URL.revokeObjectURL(created); };
  }, [doc, fetchBlob]);

  return (
    <div className="border rounded-sm overflow-hidden flex flex-col" data-testid={`doc-card-${doc.doc_type}`}>
      <button
        onClick={() => doc.available && onOpen(doc)}
        disabled={!doc.available}
        className="relative h-32 bg-muted/40 flex items-center justify-center overflow-hidden group disabled:cursor-not-allowed"
        data-testid={`doc-preview-${doc.doc_type}`}
      >
        {!doc.available ? (
          <div className="flex flex-col items-center text-muted-foreground text-xs gap-1"><AlertTriangle className="h-6 w-6 text-orange-500" />File unavailable</div>
        ) : doc.is_image ? (
          thumb ? <img src={thumb} alt={doc.doc_label} className="h-full w-full object-cover group-hover:scale-105 transition-transform" />
            : <ImageIcon className="h-8 w-8 text-muted-foreground animate-pulse" />
        ) : (
          <div className="flex flex-col items-center text-muted-foreground"><FileText className="h-9 w-9" /><span className="text-[10px] mt-1 font-semibold">PDF</span></div>
        )}
        {doc.available && <span className="absolute inset-0 bg-black/0 group-hover:bg-black/20 flex items-center justify-center opacity-0 group-hover:opacity-100 transition"><Eye className="h-5 w-5 text-white" /></span>}
      </button>
      <div className="p-2 space-y-2 flex-1 flex flex-col">
        <div>
          <p className="text-xs font-medium truncate" title={doc.doc_label}>{doc.doc_label}</p>
          <p className="text-[10px] text-muted-foreground truncate">{doc.original_filename}</p>
        </div>
        <Badge variant="outline" className={`rounded-sm text-[9px] w-fit ${DOC_BADGE[doc.status] || 'border-border'}`}>{(doc.status || 'uploaded').replace(/_/g, ' ').toUpperCase()}</Badge>
        {doc.status_reason && <p className="text-[10px] text-red-500 line-clamp-2">{doc.status_reason}</p>}
        <div className="flex gap-1 mt-auto">
          <Button variant="outline" size="sm" className="h-7 text-[11px] flex-1 rounded-sm" disabled={!doc.available} onClick={() => onOpen(doc)} data-testid={`doc-view-${doc.doc_type}`}><Eye className="h-3 w-3 mr-1" />View</Button>
          <Button variant="outline" size="sm" className="h-7 text-[11px] flex-1 rounded-sm" disabled={!doc.available} onClick={() => onDownload(doc)} data-testid={`doc-download-${doc.doc_type}`}><Download className="h-3 w-3" /></Button>
        </div>
        <Select value="" onValueChange={(v) => onSetStatus(v)}>
          <SelectTrigger className="h-7 text-[11px] rounded-sm" data-testid={`doc-setstatus-${doc.doc_type}`}><SelectValue placeholder="Set status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="verified">Verified</SelectItem>
            <SelectItem value="under_review">Under Review</SelectItem>
            <SelectItem value="rejected">Rejected</SelectItem>
            <SelectItem value="reupload_required">Re-upload Required</SelectItem>
          </SelectContent>
        </Select>
      </div>
    </div>
  );
}
